import type { SelectChangeEvent } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { CircularProgress, MenuItem, Select, Typography, useTheme } from '@mui/material';
import { Button as CommonBtn } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { ChevronIcon } from '@icons/ChevronIcon';
import type { TenantSsoProviderPublicDTO } from '@repositories/tenantSsoProviderRepository/tenantSsoProvider.types';
import { getSsoProviderIcon } from '@customHooks/useSsoRedirect';

type Props = {
  email: string;
  providers: TenantSsoProviderPublicDTO[];
  onBack: () => void;
  onSelectProvider: (provider: TenantSsoProviderPublicDTO) => void;
  isRedirecting: boolean;
  isItAdminPage?: boolean;
};

/**
 * SSO 프로바이더 선택 단계 (2개 이상일 때)
 */
export const SsoProviderSelectStep = ({
  email,
  providers,
  onBack,
  onSelectProvider,
  isRedirecting,
  isItAdminPage,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);

  const handleProviderLogin = () => {
    if (!selectedProviderId) return;
    const selected = providers.find(p => `${p.tenantId}:${p.registrationId}` === selectedProviderId);
    if (selected) {
      onSelectProvider(selected);
    }
  };

  return (
    <Container $isItAdminPage={isItAdminPage}>
      <ContentWrapper>
        {/* 뒤로가기 */}
        <GoBackButton onClick={onBack}>
          <ChevronIcon width={16} height={16} rotateNum={-90} color={theme.colors['ic-gray-main']} />
          <GoBackText>{t('SsoProvider.Login.BackToEmail')}</GoBackText>
        </GoBackButton>

        <TitleText variant="headline3">{t('SsoProvider.Login.Button')}</TitleText>

        {/* 이메일 배지 */}
        <EmailBadge>
          <EmailBadgeText>{email}</EmailBadgeText>
        </EmailBadge>

        <SubtitleText variant="body2" color={theme.colors['text-gray-sub-dark']}>
          {t('SsoProvider.Login.SelectGuide')}
        </SubtitleText>

        {/* 프로바이더 드롭다운 */}
        <ProviderSelect
          displayEmpty
          value={selectedProviderId ?? ''}
          onChange={(e: SelectChangeEvent<unknown>) => {
            if (typeof e.target.value === 'string') {
              setSelectedProviderId(e.target.value);
            }
          }}
          disabled={isRedirecting}
          renderValue={(value: unknown) => {
            if (typeof value !== 'string' || !value) {
              return <SelectPlaceholder>{t('SsoProvider.Login.SelectPlaceholder')}</SelectPlaceholder>;
            }
            const selected = providers.find(p => `${p.tenantId}:${p.registrationId}` === value);
            if (!selected) return null;
            return (
              <SelectedProviderRow>
                {getSsoProviderIcon(selected.issuerUri, 18)}
                <SelectedProviderName>{selected.providerName}</SelectedProviderName>
              </SelectedProviderRow>
            );
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                marginTop: '4px',
                borderRadius: '5px',
                boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.12)',
              },
            },
          }}
        >
          {providers.map(provider => {
            const providerId = `${provider.tenantId}:${provider.registrationId}`;
            return (
              <ProviderMenuItem key={providerId} value={providerId}>
                <ProviderMenuItemContent>
                  {getSsoProviderIcon(provider.issuerUri, 18)}
                  <ProviderMenuItemText>
                    <span>{provider.providerName}</span>
                    {provider.tenantName && <ProviderMenuItemSub>{provider.tenantName}</ProviderMenuItemSub>}
                  </ProviderMenuItemText>
                </ProviderMenuItemContent>
              </ProviderMenuItem>
            );
          })}
        </ProviderSelect>

        {/* 로그인 버튼 */}
        <LoginButton
          size="extraLarge"
          variant="contain"
          onClick={handleProviderLogin}
          disabled={!selectedProviderId || isRedirecting}
        >
          {isRedirecting ? <CircularProgress size={16} color="inherit" /> : t('SsoProvider.Login.LoginButton')}
        </LoginButton>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div<{ $isItAdminPage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${({ $isItAdminPage }) => ($isItAdminPage ? '194px' : '120px')} 0px 0px 0px;
  min-height: calc(100vh - 70px - var(--employee-gnb-height));
  height: 100%;
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 460px;
`;

const GoBackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 28px;

  &:hover span {
    text-decoration: underline;
  }
`;

const GoBackText = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-main']};
`;

const TitleText = styled(Typography)`
  margin-bottom: 8px;
` as typeof Typography;

const EmailBadge = styled.div`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  padding: 4px 12px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors['bg-gray-lighter']};
  border: 1px solid ${({ theme }) => theme.colors['border-gray-lighter']};
  margin-bottom: 8px;
`;

const EmailBadgeText = styled.span`
  ${fonts.Caption2};
  color: ${({ theme }) => theme.colors['text-gray-sub-dark']};
`;

const SubtitleText = styled(Typography)`
  margin-bottom: 20px;
`;

const ProviderSelect = styled(Select)`
  width: 100%;
  height: 44px;
  border-radius: 5px;
  margin-bottom: 20px;

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-light']};
    border-radius: 5px;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-gray-dark']};
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) => theme.colors['border-primary']};
  }

  & .MuiSelect-select {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    height: 100% !important;
    box-sizing: border-box;
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
  }
`;

const SelectPlaceholder = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const SelectedProviderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SelectedProviderName = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-main']};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProviderMenuItem = styled(MenuItem)`
  padding: 10px 12px;

  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }

  &.Mui-selected {
    background-color: ${({ theme }) => theme.colors['state-white-primary-pressed']};

    &:hover {
      background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
    }
  }
`;

const ProviderMenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ProviderMenuItemText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  & > span {
    ${fonts.Body2};
    color: ${({ theme }) => theme.colors['text-gray-main']};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ProviderMenuItemSub = styled.span`
  ${fonts.Caption2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
`;

const LoginButton = styled(CommonBtn)`
  width: 100%;
  height: 32px;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  color: ${({ theme }) => theme.colors['text-white']};
  ${fonts.Button2};

  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }

  &:disabled {
    opacity: 0.5;
  }
`;
